import React, {useId, useState} from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useBreakpointValue} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'
import PDCTitle from '../../../components/pdc-title'
import Carousel from '../../../components/carousel'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element'
import {ArrowButton} from '../../../components/arrow-button'
import {generateResponsivePaddingRightForSlider} from '../../../utils/utils'

/**
 * PDCAboutIconicBlock is a flexible, responsive React component designed for the Page Designer platform,
 * rendering an "About Iconic" content block with support for dynamic titles, subtitles, descriptions, and
 * a configurable set of tiles. The component adapts its layout based on screen size, displaying a carousel
 * for mobile or a grid for larger screens, and supports internationalization and accessibility features.
 *
 * Props:
 * @param {string} title - The main title of the block.
 * @param {string|number} titleHeadingLevel - The heading level for the title (e.g., 'h2', 2).
 * @param {string} titleFontColor - The color of the title text.
 * @param {string} suptitle - Optional subtitle displayed above the main title.
 * @param {string} desc - Optional description text below the title.
 * @param {string} textColor - The color applied to text elements.
 * @param {string} txtHorizontalPos - Horizontal alignment for text ('left' or 'center').
 * @param {number} smallNbSlidesToShow - Number of tiles to show on small screens.
 * @param {number} mediumNbSlidesToShow - Number of tiles to show on medium screens.
 * @param {number} largeNbSlidesToShow - Number of tiles to show on large screens.
 * @param {number} xlargeNbSlidesToShow - Number of tiles to show on extra-large screens.
 * @param {number} xxlargeNbSlidesToShow - Number of tiles to show on double extra-large screens.
 * @param {number} displayPriority - Priority value passed to each tile for display logic.
 * @param {Array} regions - Array of region objects containing tile and link components.
 *
 * Features:
 * - Dynamically determines the number of tiles to display per breakpoint.
 * - Renders a carousel for mobile if the number of tiles exceeds the visible slides.
 * - Renders a grid layout for larger screens or when tiles fit within the visible area.
 * - Supports custom link components and flexible content regions.
 * - Integrates with internationalization for accessibility labels.
 * - Applies custom styles and layout based on props and breakpoints.
 *
 * Usage:
 * Use this component within a Page Designer layout to showcase a set of iconic features, products,
 * or highlights, with optional supporting text and links. Configure the number of visible tiles per
 * breakpoint for optimal responsiveness.
 */
export const PDCAboutIconicBlock = ({
    title,
    titleHeadingLevel,
    titleFontColor,
    suptitle,
    desc,
    textColor,
    txtHorizontalPos,
    smallNbSlidesToShow,
    mediumNbSlidesToShow,
    largeNbSlidesToShow,
    xlargeNbSlidesToShow,
    xxlargeNbSlidesToShow,
    displayPriority,
    regions
}) => {
    const intl = useIntl()
    const titleId = useId()
    const [swiper, setSwiper] = useState()
    let tiles = []
    let link = null

    const txtPosFlex = {
        left: 'flex-start',
        center: 'center'
    }

    // Get the tiles and link components from the regions
    if (regions) {
        regions.forEach((region) => {
            if (region.id === 'pdc_about_iconic_block_tiles') {
                tiles = region.components
            } else if (
                region.id === 'pdc_about_iconic_block_links' &&
                region.components &&
                region.components.length > 0
            ) {
                link = region.components[0]
            }
        })
    }

    // Get the number of slides to show depending on the screen size
    const nbSlidesToShowByBreakpoint = usePDCResponsiveElement({
        smallEl: smallNbSlidesToShow || 1,
        mediumEl: mediumNbSlidesToShow || 2,
        largeEl: largeNbSlidesToShow,
        xlargeEl: xlargeNbSlidesToShow,
        xxlargeEl: xxlargeNbSlidesToShow
    })
    const currentNbSlidesToShow = useBreakpointValue(nbSlidesToShowByBreakpoint)

    const carouselSettings = {
        dots: false,
        infinite: true,
        arrows: true,
        speed: 500,
        slidesToShow: currentNbSlidesToShow,
        prevArrow: <ArrowButton direction="prev" onClick={() => swiper?.slickPrev()} />,
        nextArrow: <ArrowButton direction="next" onClick={() => swiper?.slickNext()} />
    }

    return (
        <Box
            as="section"
            aria-describedby={titleId}
            color={textColor}
            pb={[53, null, null, 29]}
            css={[PDCAboutIconicBlock.css(nbSlidesToShowByBreakpoint)]}
        >
            <Flex
                direction="column"
                alignItems={txtPosFlex[txtHorizontalPos]}
                maxWidth={txtHorizontalPos === 'center' ? '593px' : 'none'}
                flex={1}
                pt={['70px', null, '64px']}
                pb={['35px', null, '29px']}
                px={['20px', null, '30px']}
                mx="auto"
            >
                {suptitle && (
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: suptitle
                        }}
                        variant="descriptor"
                        color={textColor ? textColor : 'fullBlack'}
                        mb={[10, null, '24px']}
                        align={txtHorizontalPos}
                    />
                )}
                <PDCTitle
                    width={'100%'}
                    title={title}
                    titleFontColor={titleFontColor}
                    titleHeadingLevel={titleHeadingLevel}
                    textColor={textColor}
                    txtHorizontalPos={txtHorizontalPos}
                    id={titleId}
                    pt={[10, null, 10]}
                    size="5xl"
                />

                {desc && (
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: desc
                        }}
                        variant="bodyLarge2"
                        pt={['18px', null, '8px']}
                        color={textColor || 'darkGray'}
                        align={txtHorizontalPos}
                    />
                )}
                {link && (
                    <Box pt={12} align={txtHorizontalPos}>
                        <Component component={link} />
                    </Box>
                )}
            </Flex>
            {/* Display a carousel if there are more tiles than the number of slides to show */}
            {tiles && tiles.length > currentNbSlidesToShow ? (
                <Carousel
                    settings={carouselSettings}
                    label={intl.formatMessage({
                        id: 'about.iconic.block.carousel.label',
                        defaultMessage: 'About Iconic carousel'
                    })}
                    setSwiper={setSwiper}
                    nbSlidesToShowByBreakpoint={nbSlidesToShowByBreakpoint}
                >
                    {tiles.map((tile, index) => {
                        tile.displayPriority = displayPriority
                        tile.nbSlides = currentNbSlidesToShow
                        return <Component key={tile?.id || index} component={tile} />
                    })}
                </Carousel>
            ) : (
                <Flex
                    as="ul"
                    listStyleType={'none'}
                    justifyContent="center"
                    maxWidth={['none', null, '75%']}
                    mx="auto"
                    sx={{
                        '& > *': {
                            flex: 1
                        }
                    }}
                >
                    {tiles.map((tile, index) => (
                        <li key={tile?.id || index}>
                            <Component component={tile} />
                        </li>
                    ))}
                </Flex>
            )}
        </Box>
    )
}

PDCAboutIconicBlock.css = (nbSlidesToShow) => css`
    .slick-slider {
        padding-top: 5px;
        padding-bottom: 5px;
        padding-right: min(calc(122px), calc(100vw / 2));
    }
    .slick-list {
        overflow: visible;
    }
    .slick-arrow {
        opacity: 0;
        pointer-events: none;
        position: absolute;
        left: 42px;
        bottom: 40px;
        top: 171px;
        width: 40px;
        height: 40px;
        background: white;
        z-index: 1;
        transform: none;
        color: var(--chakra-colors-fullBlack);
        border: 1px solid var(--chakra-colors-gray);
        &::before {
            content: '';
        }
        &:focus {
            opacity: 1;
        }

        @media screen and (min-width: 1200px) {
            opacity: 1;
            pointer-events: auto;
            top: 179px;
        }
    }
    .slick-next {
        left: auto;
        right: 42px;
    }

    ${generateResponsivePaddingRightForSlider(122, nbSlidesToShow, 2)}
`

PDCAboutIconicBlock.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    suptitle: PropTypes.string,
    desc: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    txtHorizontalPos: PropTypes.string,
    smallNbSlidesToShow: PropTypes.number,
    largeNbSlidesToShow: PropTypes.number,
    mediumNbSlidesToShow: PropTypes.number,
    xlargeNbSlidesToShow: PropTypes.number,
    xxlargeNbSlidesToShow: PropTypes.number,
    displayPriority: PropTypes.bool,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
